import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import { ApiResponse, PaginatedApiResponse } from 'types/ApiResponse';
import { Employee, CreateEmployeeData, EmployeesState, GetUsersByCompanyParams } from './types';

// Initial state
export const initialState: EmployeesState = {
  employees: [],
  loading: false,
  error: null,
  modalOpen: false,
  searchQuery: '',
  currentPage: 1,
  pageSize: 50,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    perPage: 50,
  },
};

// RTK Slice
const slice = createSlice({
  name: 'employees',
  initialState,
  reducers: {
    openModal: (state) => {
      state.modalOpen = true;
    },
    closeModal: (state) => {
      state.modalOpen = false;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.currentPage = 1; // Reset to first page on search
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page on page size change
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    resetFilters: (state) => {
      state.searchQuery = '';
      state.currentPage = 1;
      state.sortBy = 'createdAt';
      state.sortOrder = 'desc';
    },
    setEmployees: (state, action: PayloadAction<Employee[]>) => {
      state.employees = action.payload;
    },
    updateEmployee: (state, action: PayloadAction<Employee>) => {
      const index = state.employees.findIndex(emp => emp.id === action.payload.id);
      if (index !== -1) {
        state.employees[index] = action.payload;
      }
    },
    removeEmployee: (state, action: PayloadAction<string>) => {
      state.employees = state.employees.filter(emp => emp.id !== action.payload);
    },
    setPagination: (state, action: PayloadAction<EmployeesState['pagination']>) => {
      state.pagination = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

// RTK Query API
export const employeesApi = createApi({
  reducerPath: 'employeesApi',
  baseQuery,
  tagTypes: ['Employee'],
  endpoints: (builder) => ({
    getEmployees: builder.query<PaginatedApiResponse<Employee>, { page?: number; perPage?: number }>({
      query: ({ page = 1, perPage = 10 }) => ({
        url: `${endpoints.getEmployees.url}?page=${page}&perPage=${perPage}`,
        method: endpoints.getEmployees.method,
      }),
      providesTags: ['Employee'],
    }),
    getUsersByCompany: builder.query<PaginatedApiResponse<Employee>, GetUsersByCompanyParams>({
      query: ({ companyId, page = 1, limit = 50, search, sortBy, sortOrder }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });

        if (search) {
          params.append('search', search);
        }

        if (sortBy && sortOrder) {
          // Format sort parameter as JSON object like the API expects
          const sortObject = { [sortBy]: sortOrder === 'desc' ? -1 : 1 };
          params.append('sort', JSON.stringify(sortObject));
        }

        return {
          url: endpoints.getUsersByCompany.url.replace(':companyId', companyId) + `?${params.toString()}`,
          method: endpoints.getUsersByCompany.method,
        };
      },
      providesTags: ['Employee'],
    }),
    inviteEmployee: builder.mutation<ApiResponse<{ message: string }>, CreateEmployeeData>({
      query: (employeeData) => ({
        url: endpoints.inviteEmployee.url,
        method: endpoints.inviteEmployee.method,
        body: employeeData,
      }),
      invalidatesTags: ['Employee'],
    }),
    deleteUser: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: (userId) => ({
        url: endpoints.deleteUser.url.replace(':id', userId),
        method: endpoints.deleteUser.method,
      }),
      invalidatesTags: ['Employee'],
    }),
    updateEmployeeRole: builder.mutation<ApiResponse<Employee>, { id: string; role: 'company_admin' | 'company_user' }>({
      query: ({ id, role }) => ({
        url: endpoints.updateEmployeeRole.url.replace(':id', id),
        method: endpoints.updateEmployeeRole.method,
        body: { role },
      }),
      invalidatesTags: ['Employee'],
    }),
  }),
});

// Export hooks
export const {
  useGetEmployeesQuery,
  useLazyGetEmployeesQuery,
  useGetUsersByCompanyQuery,
  useLazyGetUsersByCompanyQuery,
  useInviteEmployeeMutation,
  useDeleteUserMutation,
  useUpdateEmployeeRoleMutation,
} = employeesApi;

// Export actions
export const { actions: employeesActions } = slice;

// Custom hook for injecting the slice
export const useEmployeesSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: employeesApi.reducerPath, reducer: employeesApi.reducer });

  return {
    actions: slice.actions,
    ...employeesApi,
    useGetEmployeesQuery,
    useLazyGetEmployeesQuery,
    useGetUsersByCompanyQuery,
    useLazyGetUsersByCompanyQuery,
    useInviteEmployeeMutation,
    useDeleteUserMutation,
    useUpdateEmployeeRoleMutation,
  };
}; 